<template>
  <div class="min-h-screen bg-[#f6f7f9] flex items-center py-12">
    <div class="max-w-[680px] mx-auto px-6 w-full text-center">

      <!-- 404 -->
      <div class="text-[8rem] font-black leading-none mb-3 select-none" style="background: linear-gradient(135deg, #FFD500, #FFA800); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
        404
      </div>

      <h1 class="text-[2rem] font-extrabold text-[#111] mb-3">Pagina niet gevonden</h1>
      <p class="text-[1rem] text-[#666] mb-8 leading-relaxed">
        De pagina die je zoekt bestaat niet of is verplaatst.<br>
        Zoek direct een product, of ga terug via de links hieronder.
      </p>

      <!-- Zoekbalk -->
      <form @submit.prevent="search" class="flex gap-2 mb-10 max-w-[480px] mx-auto">
        <div class="flex-1 relative">
          <i class="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#aaa]"></i>
          <input
            v-model="query"
            type="search"
            placeholder="Zoek een product…"
            class="w-full pl-10 pr-4 py-3 border border-[#e5e7eb] rounded-xl text-[0.9rem] text-[#111] bg-white focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors"
          >
        </div>
        <button
          type="submit"
          class="bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-3 rounded-xl transition-colors shrink-0"
        >
          Zoeken
        </button>
      </form>

      <!-- Quick links -->
      <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 mb-5">
        <h3 class="text-[1rem] font-bold text-[#111] mb-4">Populaire categorieën</h3>
        <div class="grid grid-cols-2 gap-2.5">
          <router-link
            v-for="link in quickLinks"
            :key="link.to"
            :to="link.to"
            class="flex items-center gap-3 bg-[#f8f9fa] hover:bg-[#fffbeb] border-2 border-transparent hover:border-[#FFD500] px-4 py-3 rounded-xl no-underline text-[#333] font-semibold text-[0.875rem] nf-quicklink"
          >
            <i :class="['bi text-[1.1rem] text-[#FFD500]', link.icon]"></i>
            {{ link.label }}
          </router-link>
        </div>
      </div>

      <!-- CTAs -->
      <div class="flex gap-3 justify-center flex-wrap mb-5">
        <router-link
          to="/"
          class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-3 rounded-xl no-underline nf-cta-primary"
        >
          <i class="bi bi-house"></i>
          Terug naar home
        </router-link>
        <router-link
          to="/producten"
          class="inline-flex items-center gap-2 bg-white border-2 border-[#e9ecef] hover:border-[#FFD500] text-[#333] font-bold px-6 py-3 rounded-xl no-underline nf-quicklink"
        >
          <i class="bi bi-grid"></i>
          Alle producten
        </router-link>
      </div>

      <!-- Help -->
      <div class="bg-white border border-[#e9eaec] rounded-2xl p-5 flex items-start gap-4 text-left">
        <i class="bi bi-headset text-[2.2rem] text-[#FFD500] shrink-0 mt-0.5"></i>
        <div>
          <strong class="block text-[0.95rem] font-bold text-[#111] mb-0.5">Hulp nodig?</strong>
          <p class="text-[0.8rem] text-[#666] mb-2">Onze experts helpen je graag verder via mail of chat.</p>
          <div class="flex gap-3 flex-wrap">
            <a href="mailto:info@plug2charge.nl" class="inline-flex items-center gap-1.5 text-[0.82rem] font-bold text-[#111] hover:text-[#FFD500] no-underline transition-colors">
              <i class="bi bi-envelope"></i> info@plug2charge.nl
            </a>
            <a href="/klantenservice#livechat" class="inline-flex items-center gap-1.5 text-[0.82rem] font-bold text-[#111] hover:text-[#FFD500] no-underline transition-colors">
              <i class="bi bi-chat-dots"></i> Live chat
            </a>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const query  = ref('')

const search = () => {
  if (query.value.trim()) {
    router.push({ path: '/producten', query: { s: query.value.trim() } })
  }
}

const quickLinks = [
  { to: '/categorie/laadpalen',       icon: 'bi-ev-station',       label: 'Laadpalen' },
  { to: '/categorie/thuisbatterijen', icon: 'bi-battery-charging',  label: 'Thuisbatterijen' },
  { to: '/categorie/zonnepanelen',    icon: 'bi-sun',               label: 'Zonnepanelen' },
  { to: '/categorie/laadkabels',      icon: 'bi-plug',              label: 'Laadkabels' },
  { to: '/acties',                    icon: 'bi-tag',               label: 'Aanbiedingen' },
  { to: '/merken',                    icon: 'bi-award',             label: 'Merken' },
]

onMounted(() => {
  document.title = 'Pagina niet gevonden | Plug2Charge.nl'
})
</script>

<style scoped>
.nf-quicklink {
  transition: border-color 150ms cubic-bezier(0.23, 1, 0.32, 1),
              background 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.nf-cta-primary {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.nf-cta-primary:hover  { transform: translateY(-1px); }
.nf-cta-primary:active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) {
  .nf-quicklink, .nf-cta-primary { transition: none; }
}
</style>
